import React from "react";
import Hero from "../components/Hero";
import ImageSlider from "../components/ImageSlider";
import FeaturedEvents from "../components/FeaturedEvents";
import LatestNews from "../components/LatestNews";

const Home = () => {
  const [modalEvent] = React.useState(null);
  const [modalNews] = React.useState(null);
  const [lightboxImage, setLightboxImage] = React.useState(null); // State for lightbox image display

  // Carousel images - using available images in public folder
  const importAll = (r) =>
    r.keys().map((fileName) => ({
      src: r(fileName),
      title: fileName.replace("./", ""),
      description: "Gallery Image",
    }));

  const galleryImages = importAll(
    require.context("../assets/gallery", false, /\.(png|jpe?g|webp|gif)$/)
  );

  // No additional state for gallery - all handled in the RandomPopupGallery component

  // Prevent background scroll when any modal is open
  React.useEffect(() => {
    if (modalEvent || modalNews || lightboxImage) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }
    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, [modalEvent, modalNews, lightboxImage]);

  return (
    <div className="relative min-h-screen overflow-x-hidden max-w-[100vw]">
      {/* Full-page animated/gradient background matching News page */}
      <div
        className="fixed inset-0 -z-10 bg-gradient-to-br from-blue-200 via-white to-blue-400 animate-fade-in overflow-hidden"
        style={{
          width: "100%",
          height: "100%",
          backgroundAttachment: "fixed",
          backgroundSize: "100% 100%",
          backgroundPosition: "center",
          left: 0,
          top: 0,
        }}
        aria-hidden="true"
      />

      {/* Responsive Lightbox Modal for Gallery Images */}
      {lightboxImage && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-2 sm:p-4"
          onClick={() => setLightboxImage(null)}
        >
          <div
            className="relative w-full h-full sm:max-w-5xl sm:max-h-[90vh] bg-black/70 rounded-lg overflow-hidden flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute top-2 right-2 z-10 bg-black/60 hover:bg-black/80 text-white rounded-full p-2 sm:p-3 transition-colors duration-200"
              onClick={() => setLightboxImage(null)}
              aria-label="Close image"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 sm:h-6 sm:w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            <img
              src={lightboxImage.src}
              alt={lightboxImage.title}
              className="max-w-full max-h-full object-contain"
              style={{
                maxHeight:
                  window.innerWidth < 640 ? "calc(100vh - 4rem)" : "85vh",
                width: "auto",
                height: "auto",
              }}
            />
          </div>
        </div>
      )}

      <Hero />

      {/* News and Events Section with Dark Theme */}
      <section className="relative pt-0 pb-10 -mt-20 bg-gray-900 text-white overflow-hidden">
        {/* No Decorative Wave Top Border - connecting directly to Hero */}

        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div
            className="absolute top-0 left-0 w-full h-full"
            style={{
              backgroundImage:
                "radial-gradient(circle, rgba(59, 130, 246, 0.2) 1px, transparent 1px)",
              backgroundSize: "30px 30px",
            }}
          ></div>
        </div>

        {/* Glowing orbs */}
        <div className="container mx-auto px-6 pt-20 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Events Column */}
            <div className="flex flex-col h-full">
              <FeaturedEvents />
            </div>

            {/* News Column */}
            <div className="flex flex-col h-full">
              <LatestNews />
            </div>
          </div>
        </div>
      </section>

      {/* Modern Image Gallery Section */}
      <section className="relative py-12 sm:py-16 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 pointer-events-none select-none">
          <div className="w-full h-full bg-gradient-to-tr from-blue-200/40 via-blue-100/20 to-transparent blur-xl" />
        </div>
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center mb-8 sm:mb-10 animate-fade-in-up">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold bg-gradient-to-r from-gray-900 via-blue-700 to-gray-900 bg-clip-text text-transparent mb-4 sm:mb-6 animate-fade-in px-4">
              Moments That Matter
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed px-4 sm:px-6">
              Explore our vibrant community through these captured moments at
              University of Moratuwa Mechanical Engineering Society.
            </p>
          </div>

          {/* Modern Image Slider */}
          <div className="w-full max-w-7xl mx-auto mb-8">
            <ImageSlider
              images={galleryImages}
              onImageClick={setLightboxImage}
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
